/*
 * product-purchase-form.js — Variant resolution + URL push +
 * H3.0 variant:update dispatch + ATC button state machine.
 *
 * Attaches one instance per `.product-purchase-form` on the page.
 * Reads the embedded product-json blob to map selected option
 * values back to a variant; on any option change:
 *
 *   - Updates the form's hidden `id` input to the new variant id.
 *   - Pushes `?variant=<id>` to the URL (replaceState so back
 *     button still works, but no extra history entry per click).
 *   - Dispatches a `variant:update` CustomEvent with the same
 *     payload shape H3.0's native `VariantUpdateEvent` uses
 *     (`event.detail.resource = variant`). The PDP's gallery
 *     (assets/component-main-product-gallery.js) and badges
 *     (assets/product-page-badges.js) both already listen for
 *     this and react.
 *   - Resolves the ATC button state from the variant and applies
 *     it (label / class / type / disabled). Five states:
 *       atc       Add to Cart       (default, type=submit)
 *       preorder  Pre-Order         (type=submit, still buyable)
 *       oos       Out of Stock      (disabled, type=button)
 *       notify    Notify Me ...     (type=button, dispatches
 *                                    `pdp:open-notify`)
 *       enquiry   Make an Enquiry   (type=button, dispatches
 *                                    `pdp:open-enquiry`)
 *     Made-to-order metafield (custom.made_to_order) → enquiry;
 *     preorder metafield (smart.pre_order + no stock) → preorder;
 *     unavailable + no backorders → notify (if modal block
 *     present) or oos; everything else → atc.
 *
 * Initial load — if `?variant=<id>` is in the URL, that variant
 * trumps the server-rendered `selected_or_first_available_variant`
 * and the picker repaints to match (radio / select state is set
 * via the option name → input value lookup). Otherwise the
 * picker uses whatever Shopify already chose server-side. State
 * is applied on first paint either way.
 */

import { formatMoney } from '@theme/money-formatting';

const SELECTOR = '.product-purchase-form';
const INSTANCE_KEY = '_productPurchaseFormInstance';

function normalizeOptionValue(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/[\s_\-:]+/g, '')
    .trim();
}

/** Match a desired value to a real <select> option (handles 14k vs 14K casing). */
function resolveSelectValue(select, value) {
  if (!select || value == null || value === '') return value;
  if (Array.from(select.options).some((option) => option.value === value)) return value;

  const target = normalizeOptionValue(value);
  const normalized = Array.from(select.options).find(
    (option) => normalizeOptionValue(option.value) === target
  );
  if (normalized) return normalized.value;

  const parts = String(value).split(' / ');
  if (parts.length >= 2) {
    const fuzzy = Array.from(select.options).find((option) => {
      const segments = String(option.value).split(' / ');
      if (segments.length !== parts.length) return false;
      return segments.every((segment, index) => normalizeOptionValue(segment) === normalizeOptionValue(parts[index]));
    });
    if (fuzzy) return fuzzy.value;
  }

  return value;
}

function variantOptionValue(variant, position) {
  if (window.ProductVariantMatrix?.optionValue) {
    return window.ProductVariantMatrix.optionValue(variant, position);
  }
  return variant?.['option' + position] ?? null;
}

/**
 * VariantResolver — the single owner of "selected options → variant".
 *
 * Layered lookup, cheapest first:
 *   1. Memory cache, keyed `${handle}::${options.join('|')}` — handle-scoped
 *      so entries survive a metal-switch repoint (each sibling keeps its own).
 *   2. The product's in-memory variants array via `findLocal` (the embedded
 *      250-cap JSON, or the full paginated matrix once
 *      product-variant-matrix.js delivers it). NOT a droppable cache:
 *      compound-option graying, accordion delta pricing and sibling matching
 *      all need the full array as a set — only single-variant resolution
 *      lives here.
 *
 * Capped products (>250 variants) load their full matrix eagerly at init
 * (ProductPurchaseForm constructor → _maybeLoadFullVariants), so layer 2 is
 * authoritative for every combo. A combo not found locally resolves to null
 * and the caller keeps the current variant; once the matrix lands,
 * _applyFullVariants re-resolves the live selection.
 *
 * Resolution is entirely in-memory and synchronous. The full variant matrix is
 * already needed in memory for compound-option graying and sibling matching, so
 * resolving against it there keeps every option change off the network and off
 * the shopper's critical path, and needs no async coordination. The only cost
 * is ~2 paginated requests at init on capped products — the exact population
 * whose option changes most need instant resolution.
 */
class VariantResolver {
  #cache = new Map();
  #getHandle;
  #findLocal;

  /**
   * @param {object} deps
   * @param {() => string} deps.getHandle - current product handle (live —
   *   the metal switch updates it before repointing the form).
   * @param {(options: (string|null)[]) => object|null} deps.findLocal -
   *   scan of the product's current in-memory variants array.
   */
  constructor({ getHandle, findLocal }) {
    this.#getHandle = getHandle;
    this.#findLocal = findLocal;
  }

  /**
   * Set with a bound on cache size. Entries are handle-scoped so they survive a
   * metal-switch repoint (each sibling keeps its own — see class doc), but the
   * map is otherwise unbounded across visited option combos. Cap at 100 with
   * oldest-first eviction (Map preserves insertion order).
   */
  #cacheSet(key, value) {
    this.#cache.set(key, value);
    if (this.#cache.size > 100) {
      this.#cache.delete(this.#cache.keys().next().value);
    }
  }

  /**
   * @param {(string|null)[]} options - selected option values by position.
   * @returns {object|null} resolved variant, or null when the combo isn't in
   *   the in-memory matrix (caller keeps the previous variant).
   */
  resolve(options) {
    const key = `${this.#getHandle()}::${options.join('|')}`;
    const hit = this.#cache.get(key);
    if (hit) return hit;

    const local = this.#findLocal(options);
    if (local) {
      this.#cacheSet(key, local);
      return local;
    }

    return null;
  }
}

class ProductPurchaseForm {
  constructor(root) {
    this.root = root;
    this.form = root.querySelector('.product-purchase-form__form');
    this.variantInput = root.querySelector('[data-product-form-variant-id]');
    this.optionInputs = Array.from(root.querySelectorAll('[data-option-select]'));
    this.atcButton = root.querySelector('[data-purchase-form-atc]');
    this.atcLabel = root.querySelector('.product-purchase-form__atc-text');

    this.productJson = this._readProductJson();
    if (!this.productJson) return;

    this.variants = this.productJson.variants || [];
    this.optionsCount = (this.productJson.options || []).length;
    this._resolver = new VariantResolver({
      getHandle: () => this._productHandle(),
      findLocal: (options) => this._findVariant(options),
    });
    this.mtoMap = this._readMtoMap();
    this.preorderMap = this._readPreorderMap();
    // Modal-presence detection runs in JS rather than server-side.
    // section.blocks is unreliable to iterate from inside both
    // rendered snippets and block files in Horizon — the
    // modal elements being present in the rendered DOM is the
    // authoritative source of truth and JS can read it directly.
    const section = root.closest('.shopify-section');
    const scope = section || document;
    this.enquiryEnabled = !!scope.querySelector('enquiry-modal-component');
    this.notifyEnabled = !!scope.querySelector('notify-me-modal-component');

    this.abortController = new AbortController();
    this._bind();
    this._bindAtcStateClicks();
    this._bindStateRequests();
    this._bindSelectionsComplete();
    this._syncFromUrl();
    // Eager full-matrix load. In-memory resolution is the only path (see
    // VariantResolver doc), so the matrix must be authoritative before the
    // shopper can change options — hence no requestIdleCallback defer.
    // _maybeLoadFullVariants no-ops for products already under the 250 cap, so
    // running it eagerly for everyone is free on the common path; only capped
    // products pay the ~2 paginated requests, and they pay them here rather
    // than mid-interaction. It stays an optimisation, not a correctness
    // dependency: if an option changes before the matrix lands,
    // _applyFullVariants re-resolves the live selection when it does.
    this._maybeLoadFullVariants();

    // Initial state — `_syncFromUrl` already calls `_setCurrentVariant`
    // when ?variant= is in the URL, which applies the state. Cover the
    // no-URL-param case explicitly so first paint matches the
    // selected_or_first_available_variant Shopify chose server-side.
    const initialId = this.variantInput?.value;
    if (initialId) {
      const initialVariant = this.variants.find(
        (v) => String(v.id) === String(initialId)
      );
      if (initialVariant) {
        this._applyState(this._resolveState(initialVariant), initialVariant);
      }
    }
  }

  _readMtoMap() {
    const node = this.root.querySelector('.product-purchase-form__mto-map');
    if (!node) return {};
    try {
      return JSON.parse(node.textContent);
    } catch (_e) {
      return {};
    }
  }

  _readPreorderMap() {
    const node = this.root.querySelector('.product-purchase-form__preorder-map');
    if (!node) return {};
    try {
      return JSON.parse(node.textContent);
    } catch (_e) {
      return {};
    }
  }

  _readProductJson() {
    const node = this.root.querySelector('.product-purchase-form__product-json');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (_e) {
      return null;
    }
  }

  _bind() {
    this.optionInputs.forEach((input) => {
      input.addEventListener('change', () => this._handleChange(), {
        signal: this.abortController.signal,
      });
    });
  }

  /**
   * Read the currently-checked / selected value for each option
   * position (1-indexed in Shopify). Returns an array indexed by
   * (position - 1) so `selected[0]` is option 1, etc.
   *
   * Custom Select dropdowns render as <custom-select-component>; the
   * actual value lives on the inner hidden input. The host element
   * itself carries the data-option-* attributes so binding stays
   * generic.
   */
  _readSelectedOptions() {
    const selected = new Array(this.optionsCount).fill(null);
    this.optionInputs.forEach((el) => {
      const pos = parseInt(el.dataset.optionPosition, 10);
      if (!pos) return;
      const idx = pos - 1;

      if (el.tagName === 'CUSTOM-SELECT-COMPONENT') {
        const hidden = el.querySelector('input[type="hidden"]');
        if (hidden) selected[idx] = hidden.value;
      } else if (el.tagName === 'SELECT') {
        selected[idx] = el.value;
      } else if (el.type === 'radio') {
        if (el.checked) selected[idx] = el.value;
      } else {
        // Defensive — handles any other input type Shopify might add later.
        if (el.value) selected[idx] = el.value;
      }
    });
    return selected;
  }

  /** Re-apply ATC state for whatever variant id the form currently holds. */
  _restoreAtcState() {
    const id = this.variantInput?.value;
    const variant = this.variants.find((v) => String(v.id) === String(id)) || null;
    this._applyState(this._resolveState(variant), variant);
  }

  _findVariant(selectedOptions) {
    if (!selectedOptions.length) return null;
    if (!selectedOptions.every((v) => v != null && v !== '')) return null;

    const matchesSelection = (variant, skipEmptyVariantOptions) =>
      selectedOptions.every((val, i) => {
        if (val == null || val === '') return true;
        const variantVal = variantOptionValue(variant, i + 1);
        if (variantVal == null && skipEmptyVariantOptions) return true;
        return variantVal === val;
      });

    const exact = this.variants.find((v) => matchesSelection(v, false));
    if (exact) return exact;

    const candidates = this.variants.filter((v) => matchesSelection(v, true));
    if (!candidates.length) return null;
    if (candidates.length === 1) return candidates[0];

    const optionNames = this.productJson.options || [];
    const compoundIdx = optionNames.findIndex(
      (name) => name && String(name).indexOf(':') !== -1
    );
    if (compoundIdx >= 0 && selectedOptions[compoundIdx]) {
      const compoundMatches = candidates.filter(
        (v) => variantOptionValue(v, compoundIdx + 1) === selectedOptions[compoundIdx]
      );
      if (compoundMatches.length) {
        return compoundMatches.find((v) => v.available) || compoundMatches[0];
      }
    }

    return candidates.find((v) => v.available) || candidates[0];
  }

  // Synchronous by design: resolve() is a pure in-memory lookup, so each change
  // handler runs to completion before the next starts and the last click wins.
  // No await can interleave a stale result between a click and its
  // variant:update dispatch.
  _handleChange() {
    const selected = this._readSelectedOptions();
    const variant = this._resolver.resolve(selected);

    if (variant) {
      this._setCurrentVariant(variant);
    } else {
      // Unresolvable combo — keep the previous variant, just restore the ATC.
      this._restoreAtcState();
    }
  }

  _setCurrentVariant(variant) {
    if (this.variantInput) this.variantInput.value = variant.id;
    this._pushVariantUrl(variant.id);
    this._dispatchVariantUpdate(variant);
    this._applyState(this._resolveState(variant), variant);
  }

  /**
   * Resolve the ATC button state from a variant. Mirrors v3.0's
   * sa-product-form precedence, extended with a Pre-Order state:
   *
   *   1. Made-to-order (custom.made_to_order metafield) → 'enquiry'
   *   2. Pre-order (smart.pre_order metafield + no current stock)
   *      → 'preorder'
   *   3. Unavailable + no backorders → 'notify' (when enabled) or 'oos'
   *   4. Otherwise → 'atc'
   *
   * Pre-order check matches the v3.0 preorder-notice snippet's show
   * logic — the flag alone isn't enough; the variant also needs to be
   * out of stock (inventory_quantity <= 0). This way a merchant who
   * leaves the flag on after restocking doesn't get a "Pre-Order"
   * button on an in-stock item.
   *
   * Backorders are allowed when Shopify isn't tracking inventory
   * (inventory_management !== 'shopify') OR the policy is
   * 'continue' (sell when out of stock). When backorders are
   * allowed, an unavailable variant still resolves to 'atc' — the
   * customer can still buy.
   *
   * Modal-dependent states (enquiry, notify) only return when the
   * matching modal block is present in the section; without the
   * modal the state machine falls through to its non-modal sibling
   * (enquiry → atc; notify → oos) so the button stays usable.
   */
  _resolveState(variant) {
    if (!variant) return 'atc';

    const isMto = this.mtoMap[String(variant.id)] === true;
    if (isMto) {
      return this.enquiryEnabled ? 'enquiry' : 'atc';
    }

    const preorder = this.preorderMap[String(variant.id)];
    if (
      preorder &&
      preorder.pre_order === true &&
      (preorder.inventory_quantity == null || preorder.inventory_quantity <= 0)
    ) {
      return 'preorder';
    }

    const allowsBackorders =
      variant.inventory_management !== 'shopify' ||
      variant.inventory_policy === 'continue';

    if (variant.available || allowsBackorders) return 'atc';

    return this.notifyEnabled ? 'notify' : 'oos';
  }

  /**
   * Apply a resolved state to the ATC button: label, class
   * modifier, click target (type), and disabled. Side-effect-only;
   * the resolved state ID is the input.
   *
   * Class modifiers (.enquiry-mode / .bis-mode / .is-sold-out)
   * give the modal blocks (MP12 / MP13) a stable hook to react
   * to. The actual modal opening is dispatched via
   * `pdp:open-enquiry` / `pdp:open-notify` custom events from
   * `_bindAtcStateClicks`, scoped to the variant in detail.
   */
  _applyState(state, variant) {
    // Inline ATC may be omitted (configurator PDPs use sticky ATC only).
    // Always broadcast state so sticky / modals stay in sync.
    if (this.atcButton) {
      this.atcButton.classList.remove(
        'is-sold-out',
        'is-preorder',
        'enquiry-mode',
        'bis-mode'
      );
      this.atcButton.dataset.atcState = state;

      switch (state) {
        case 'enquiry':
          if (this.atcLabel) this.atcLabel.textContent = 'Make an Enquiry';
          this.atcButton.type = 'button';
          this.atcButton.disabled = false;
          this.atcButton.classList.add('enquiry-mode');
          this.atcButton.dataset.variantId = variant?.id ?? '';
          break;

        case 'notify':
          if (this.atcLabel) this.atcLabel.textContent = 'Notify Me When Available';
          this.atcButton.type = 'button';
          this.atcButton.disabled = false;
          this.atcButton.classList.add('bis-mode');
          this.atcButton.dataset.variantId = variant?.id ?? '';
          break;

        case 'oos':
          if (this.atcLabel) this.atcLabel.textContent = 'Out of Stock';
          this.atcButton.type = 'button';
          this.atcButton.disabled = true;
          this.atcButton.classList.add('is-sold-out');
          break;

        case 'preorder':
          if (this.atcLabel) this.atcLabel.textContent = 'Pre-Order';
          this.atcButton.type = 'submit';
          this.atcButton.disabled = false;
          this.atcButton.classList.add('is-preorder');
          break;

        case 'atc':
        default:
          if (this.atcLabel) this.atcLabel.textContent = 'Add to Cart';
          this.atcButton.type = 'submit';
          this.atcButton.disabled = false;
          break;
      }
    }

    this._dispatchStateChange(state, variant);
  }

  /**
   * Late-subscriber handshake: when modules attach after initial
   * `pdp:state-change`, they emit `pdp:request-state` and we rebroadcast
   * the resolved state so first paint is accurate.
   */
  _bindStateRequests() {
    document.addEventListener(
      'pdp:request-state',
      () => {
        const id = this.variantInput?.value;
        if (!id) return;
        const variant = this.variants.find((v) => String(v.id) === String(id));
        if (!variant) return;
        this._dispatchStateChange(this._resolveState(variant), variant);
      },
      { signal: this.abortController.signal }
    );
  }

  /**
   * Figma dev annotation (node 11012:185341, "PDP/ all selections
   * made"): once the shopper has clicked through every list-type
   * configurator accordion (assets/product-configurator-progress.js
   * dispatches this once, one-way, for the rest of the page's life),
   * flip the ATC button to its "ready" blue state. Additive to
   * `_applyState`'s stock-based class — never removed by a later
   * `_applyState` call, so an oos/preorder variant switch doesn't
   * silently undo the shopper's progress; `.is-ready` composes with
   * `:disabled` in CSS instead.
   */
  _bindSelectionsComplete() {
    document.addEventListener(
      'pdp:selections-complete',
      () => {
        this.atcButton?.classList.add('is-ready');
      },
      { signal: this.abortController.signal }
    );
  }

  /** Broadcast resolved ATC state for sibling consumers (e.g. Sticky ATC). */
  _dispatchStateChange(state, variant) {
    this.root.dispatchEvent(
      new CustomEvent('pdp:state-change', {
        bubbles: true,
        detail: {
          state,
          variant: variant || null,
          productId: this.productJson?.id ?? null,
        },
      })
    );
  }

  /**
   * Click handlers for the enquiry / notify states. Both dispatch
   * a CustomEvent (bubbling) that the matching modal block
   * listens for; the modal pulls the variant out of detail.
   *
   * Bound once at construction and gated on the button's
   * current state class so we don't accidentally hijack ATC
   * submits.
   */
  _bindAtcStateClicks() {
    if (!this.atcButton) return;
    this.atcButton.addEventListener(
      'click',
      (event) => {
        const state = this.atcButton.dataset.atcState;
        if (state !== 'enquiry' && state !== 'notify') return;
        event.preventDefault();

        const variantId = this.variantInput?.value;
        const variant = variantId
          ? this.variants.find((v) => String(v.id) === String(variantId))
          : null;

        const name = state === 'enquiry' ? 'pdp:open-enquiry' : 'pdp:open-notify';
        this.root.dispatchEvent(
          new CustomEvent(name, {
            bubbles: true,
            detail: {
              variant,
              productId: this.productJson?.id,
            },
          })
        );
      },
      { signal: this.abortController.signal }
    );
  }

  _pushVariantUrl(variantId) {
    const url = new URL(window.location.href);
    url.searchParams.set('variant', String(variantId));
    window.history.replaceState({}, '', url.toString());
  }

  /**
   * Dispatch a CustomEvent matching H3.0's VariantUpdateEvent
   * shape — `event.detail.resource` holds the variant. Bubbling so
   * section-level listeners (gallery, badges) catch it. Fired on
   * the root element so the listeners can scope by section via
   * `event.target.closest('.shopify-section')`.
   *
   * `data.html` is intentionally an empty Document — H3.0's
   * product-price / product-sku / product-inventory / media-
   * gallery / product-form listeners all call
   * `event.detail.data.html.querySelector(...)`; an empty
   * document satisfies the call, returns null for their
   * selectors, and they exit cleanly via their
   * `if (!result) return;` guards. We DON'T try to feed
   * H3.0's product-price a synthetic priceContainer to swap in
   * because its cached `refs.priceContainer` goes stale — the
   * MutationObserver that's meant to keep refs in sync attaches
   * via `requestIdleCallback` and misses early mutations, so
   * `priceContainer.replaceWith(newNode)` ends up operating on
   * a detached node and the live DOM never updates. Instead,
   * `_updateLivePriceBlocks` below mutates the live
   * priceContainer in place — bypassing H3.0's refs entirely.
   */
  _dispatchVariantUpdate(variant) {
    this._updateLivePriceBlocks(variant);
    this._updateAfterpay(variant);

    const event = new CustomEvent('variant:update', {
      bubbles: true,
      detail: {
        resource: variant,
        sourceId: this.root.dataset.productId || '',
        data: {
          productId: this.productJson.id,
          html: document.implementation.createHTMLDocument(''),
        },
      },
    });
    this.root.dispatchEvent(event);
  }

  /**
   * Mutate every `<product-price>` block belonging to this
   * product directly in the live DOM — set the new price text
   * on `.price` / `.price-item--sale.price` spans, set
   * compare-at text on `.compare-at-price`, and toggle the
   * `.price__hidden` modifier on the two outer wrappers based
   * on whether `compare_at_price > price`. In-place mutation
   * (no clone-then-replace) sidesteps H3.0's refs-going-stale
   * bug — there's nothing to swap, nothing to track. Works for
   * OOS variants too; Shopify always supplies `variant.price`.
   *
   * Money format is read from `data-money-format` on the
   * Purchase Form root (Liquid picks `shop.money_with_
   * currency_format` vs `shop.money_format` based on
   * `settings.currency_code_enabled_product_pages`) so the
   * JS-built text matches the SSR pattern exactly.
   */
  _updateLivePriceBlocks(variant) {
    if (!variant) return;

    const productId = String(this.productJson?.id ?? '');
    if (!productId) return;

    const priceElements = document.querySelectorAll(
      `product-price[data-product-id="${productId}"]`
    );
    if (!priceElements.length) return;

    const moneyFormat = this.root.dataset.moneyFormat || '${{amount}}';
    const currency = this.root.dataset.currency || 'USD';

    const priceText = formatMoney(variant.price, moneyFormat, currency);
    const compareAtRaw = variant.compare_at_price;
    const compareText =
      compareAtRaw && Number(compareAtRaw) > Number(variant.price)
        ? formatMoney(compareAtRaw, moneyFormat, currency)
        : null;
    const onSale = compareText !== null;

    priceElements.forEach((pp) => {
      const regular = pp.querySelector('.price__regular');
      if (regular) {
        regular.classList.toggle('price__hidden', onSale);
        const regSpan = regular.querySelector('.price');
        if (regSpan) regSpan.textContent = priceText;
      }

      const sale = pp.querySelector('.price__sale');
      if (sale) {
        sale.classList.toggle('price__hidden', !onSale);
        const salePrice = sale.querySelector('.price-item--sale.price');
        if (salePrice) salePrice.textContent = priceText;
        const compareAtSpan = sale.querySelector('.compare-at-price');
        if (compareAtSpan && compareText) compareAtSpan.textContent = compareText;
      }
    });
  }

  /**
   * Update the Afterpay installment price (`4x $X with`) on variant
   * change. The afterpay snippet renders the price inside
   * `[data-afterpay-price]` and the rest of the copy as siblings,
   * so we only swap the price text and never touch "4x" / "with" /
   * the icon. Scoped to the section so multiple PDPs on a page
   * don't bleed.
   *
   * Mirrors the Liquid filter chain: `price | divided_by: 4 |
   * money_without_trailing_zeros`. We divide first (integer cents),
   * then format, then strip `.00` so the SSR-vs-JS output reads
   * identical for round-number installments (`$25` not `$25.00`).
   */
  _updateAfterpay(variant) {
    if (!variant) return;

    const section = this.root.closest('.shopify-section') || document;
    const afterpayEls = section.querySelectorAll('[data-afterpay-price]');
    if (!afterpayEls.length) return;

    const moneyFormat = this.root.dataset.moneyFormat || '${{amount}}';
    const currency = this.root.dataset.currency || 'USD';

    const installmentCents = Math.round(Number(variant.price) / 4);
    const formatted = formatMoney(installmentCents, moneyFormat, currency)
      .replace(/\.00(?=\D|$)/, '');

    afterpayEls.forEach((el) => {
      el.textContent = formatted;
    });
  }

  /**
   * URL-driven initial selection. If the page loaded with a
   * `?variant=<id>` param that doesn't match what Shopify picked
   * server-side, repaint the picker to match it. Without this the
   * shared / bookmarked URL would show option A while the form
   * submitted option B.
   */
  _syncFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const urlVariantId = parseInt(params.get('variant'), 10);
    if (!urlVariantId) return;

    const variant = this.variants.find((v) => v.id === urlVariantId);
    if (!variant) return;

    // Update the inputs to match the URL variant.
    this.optionInputs.forEach((el) => {
      const pos = parseInt(el.dataset.optionPosition, 10);
      if (!pos) return;
      const target = variantOptionValue(variant, pos);
      if (target == null) return;

      if (el.tagName === 'SELECT') {
        if (el.value !== target) el.value = target;
      } else if (el.type === 'radio') {
        el.checked = el.value === target;
      }
    });

    // Push state through so gallery + badges pick the right variant
    // on initial paint (they don't otherwise know about ?variant=).
    this._setCurrentVariant(variant);
  }

  /**
   * Push an option-value array onto the live picker inputs (hidden Metal Type,
   * compound hidden select, radios). Used by the group selector after a metal
   * swap so the form + 3D bridge stay aligned with the sibling variant.
   * @param {(string|null)[]} selection
   */
  syncOptionSelection(selection) {
    if (!selection?.length) return;

    for (let i = 0; i < selection.length; i++) {
      const pos = i + 1;
      const value = selection[i];
      if (value == null || value === '') continue;

      const inputs = this.root.querySelectorAll(
        `[data-option-select][data-option-position="${pos}"]`
      );
      inputs.forEach((el) => {
        if (el.tagName === 'SELECT') {
          const resolved = resolveSelectValue(el, value);
          if (el.value !== resolved) {
            el.value = resolved;
            el.dispatchEvent(new Event('change', { bubbles: true }));
          }
          return;
        }
        if (el.type === 'radio') {
          if (el.value === value && !el.checked) {
            el.checked = true;
            el.dispatchEvent(new Event('change', { bubbles: true }));
          }
          return;
        }
        if (el.type === 'hidden' && el.value !== value) {
          el.value = value;
        }
      });
    }

    this.optionInputs = Array.from(this.root.querySelectorAll('[data-option-select]'));
  }

  repointProduct(newJson) {
    if (!newJson) return;

    const oldId = String(this.productJson?.id ?? '');
    const newId = String(newJson.id ?? '');
    if (oldId && newId && oldId !== newId) {
      document
        .querySelectorAll(`product-price[data-product-id="${oldId}"]`)
        .forEach((el) => {
          el.dataset.productId = newId;
        });
      this.root.dataset.productId = newId;
    }

    this.productJson = newJson;
    this.variants = newJson.variants || [];
    this.optionsCount = (newJson.options || []).length;
  }

  /**
   * Combined-listing metal switch — data-only, no page load, no options morph.
   *
   * Across metal siblings the Stone Type / Shape / Size option VALUES are
   * identical; only variant ids, prices and availability differ. So the
   * existing option inputs and their bound `change` listeners stay valid — we
   * just repoint this form at the sibling's product JSON and re-select the
   * matched variant. `_setCurrentVariant` then updates the ATC state, live
   * price, and dispatches `variant:update` (which the iJewel bridge consumes —
   * same cad_code → in-place metal config, no GLB reload).
   *
   * Price blocks are scoped by `data-product-id`, so they're re-pointed to the
   * sibling id here or `_updateLivePriceBlocks` would silently miss them.
   *
   * The caller (assets/product-group-selector.js) owns the surrounding steps:
   * swapping the shared product-json node text, the iJewel map global +
   * `ijewel:refresh-context`, re-initing the compound accordions with the new
   * variants, and the final sibling-URL replaceState.
   *
   * @param {object} newJson - the sibling product JSON (id, options, variants).
   * @param {object} variant - the matched variant within newJson to select.
   */
  swapProduct(newJson, variant) {
    if (!newJson || !variant) return;

    this.repointProduct(newJson);

    const selected = this._readSelectedOptions();
    const resolved = this._findVariant(selected) || variant;
    this._setCurrentVariant(resolved);
    this._maybeLoadFullVariants();
  }

  _productHandle() {
    const node = this.root.querySelector('.product-purchase-form__product-json');
    return node?.dataset?.productHandle || this.productJson?.handle || '';
  }

  _variantsHaveFullOptions(variants) {
    if (this.optionsCount <= 3 || !variants?.length) return true;
    const sample = variants.find((v) => v.available) || variants[0];
    for (let i = 4; i <= this.optionsCount; i++) {
      if (!variantOptionValue(sample, i)) return false;
    }
    return true;
  }

  _applyFullVariants(variants) {
    this.variants = variants;
    this.productJson = { ...this.productJson, variants };

    const jsonNode = this.root.querySelector('.product-purchase-form__product-json');
    if (jsonNode) jsonNode.textContent = JSON.stringify(this.productJson);

    document.dispatchEvent(
      new CustomEvent('product-variants:loaded', {
        detail: {
          variants,
          productJson: this.productJson,
          root: this.root,
        },
      })
    );

    const selected = this._readSelectedOptions();
    const variant = this._findVariant(selected);
    if (variant) this._setCurrentVariant(variant);
  }

  async _maybeLoadFullVariants() {
    const matrix = window.ProductVariantMatrix;
    const handle = this._productHandle();
    if (!matrix?.loadAll || !handle) return;

    try {
      const embeddedCount = this.variants.length;
      const needsOptions = !this._variantsHaveFullOptions(this.variants);

      const probe = await fetch(
        `/products/${encodeURIComponent(handle)}?view=variants&page=1`
      ).then((r) => (r.ok ? r.json() : null));
      if (!probe) return;

      const total = probe.total || embeddedCount;
      if (total <= embeddedCount && !needsOptions) return;

      const result = await matrix.loadAll(handle);
      if (!result.variants.length) return;

      const normalized = matrix.normalizeAll(result.variants, this.optionsCount);
      this._applyFullVariants(normalized);
    } catch (_e) {
      // Keep embedded variants — compound matching degrades gracefully.
    }
  }

  destroy() {
    this.abortController.abort();
  }
}

function initAll(root) {
  const scope = root || document;
  scope.querySelectorAll(SELECTOR).forEach((el) => {
    if (el[INSTANCE_KEY]) return;
    el[INSTANCE_KEY] = new ProductPurchaseForm(el);
  });
}

function destroyAll(root) {
  const scope = root || document;
  scope.querySelectorAll(SELECTOR).forEach((el) => {
    if (el[INSTANCE_KEY]) {
      el[INSTANCE_KEY].destroy();
      el[INSTANCE_KEY] = null;
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initAll());
} else {
  initAll();
}

// Metal-switch seam (combined listings). The group selector dispatches this
// after swapping the shared product-json node + iJewel map; we forward to the
// live instance's swapProduct(). Kept as an event (not an import) so the group
// selector needs no reference to the private instance key. Dispatch is
// synchronous, so ordering in the orchestrator's sequence is preserved.
document.addEventListener('product-form:swap-product', (e) => {
  const detail = e.detail || {};
  const root = detail.root || document.querySelector(SELECTOR);
  const instance = root && root[INSTANCE_KEY];
  if (instance) instance.swapProduct(detail.json, detail.variant);
});

document.addEventListener('product-form:repoint-product', (e) => {
  const detail = e.detail || {};
  const root = detail.root || document.querySelector(SELECTOR);
  const instance = root && root[INSTANCE_KEY];
  if (instance) instance.repointProduct(detail.json);
});

document.addEventListener('shopify:section:load', (e) => initAll(e.target));
document.addEventListener('shopify:section:unload', (e) => destroyAll(e.target));
